import React from 'react'
import styled from 'styled-components'
import { useIsClient } from 'usehooks-ts'
import {
  useAdminUser,
  useDashboardScoutsSearchFilter,
  useStartupUser,
  useUser,
} from 'apis/hooks'
import { observer } from 'mobx-react'
import {
  AdminUser,
  DashboardScoutSearchItem,
  StartupUser,
  User,
} from 'types/common'
import { Login } from 'components/misc/login'
import { Column } from 'components/common/layout'
import { useRouter } from 'next/router'
import { LockAccountProcedurePage } from 'components/dashboard/lock-account-procedure'
import { Seo } from 'components/common/seo'
import { DASHBOARD_CHAT_ROOM_PATH, HOST_BASE_URL } from 'infra/constants'
import { DeactivatedStatusPage } from 'components/misc/deactivated-status'

export const requireLogin = (Component: React.FC<{ user: User }>) => {
  return observer(() => {
    const isClient = useIsClient()
    const { data: user } = useUser()
    if (!isClient) return <Seo />
    if (!user) {
      return (
        <LoginWrapper>
          <Login />
        </LoginWrapper>
      )
    }
    if (!user.isActive) {
      return <DeactivatedStatusPage />
    }
    return <Component user={user} />
  })
}

export const requireAdminLogin = (Component: React.FC<{ user: AdminUser }>) => {
  return observer(() => {
    const isClient = useIsClient()
    const { data: user } = useAdminUser()
    if (!isClient) return null
    if (!user) {
      return (
        <LoginWrapper>
          <Login />
        </LoginWrapper>
      )
    }
    return <Component user={user} />
  })
}

export const requireStartupLogin = (
  Component: React.FC<{ user: StartupUser }>,
) => {
  return observer(() => {
    const { data: user } = useStartupUser()
    const isClient = useIsClient()
    const router = useRouter()
    if (!isClient)
      return (
        <Seo
          title='그룹바이 | 스타트업 전문 채용 플랫폼'
          description='VC에게 성장가능성을 검증받은 스타트업에 일찍 합류해보세요.'
          image='https://groupby-public-image.s3.ap-northeast-2.amazonaws.com/biz/Frame+3599.png'
          url={`${HOST_BASE_URL}/biz`}
        />
      )
    if (!user) {
      router.push(`/dashboard/login?callback=${window.location.pathname}`)
      return null
    }
    if (
      user.needChatStepCleanUp &&
      router.pathname !== DASHBOARD_CHAT_ROOM_PATH
    ) {
      router.replace(DASHBOARD_CHAT_ROOM_PATH)
      return null
    }
    return <Component user={user} />
  })
}

export const requireStartupUnlocked = (
  Component: React.FC<{ user: StartupUser }>,
) => {
  return observer(({ user }: { user: StartupUser }) => {
    if (!user.isSigned) {
      return <LockAccountProcedurePage hasToBeSigned />
    }
    return <Component user={user} />
  })
}

export const provideDashboardFilter = (
  Component: React.FC<{ filter: DashboardScoutSearchItem | null }>,
) => {
  return observer(() => {
    const router = useRouter()
    const searchId = Number(router.query.searchId)
    const { data: filter } = useDashboardScoutsSearchFilter(searchId)
    if (filter === undefined) return null
    return <Component filter={filter} />
  })
}

const LoginWrapper = styled(Column)`
  @media ${(p) => p.theme.deviceSize.desktop} {
    height: 100vh;
    width: 600px;
    margin: auto;
  }
`
